/* @flow weak */
import cx from 'classnames';
import * as React from 'react';
import Tooltip from 'components/Tooltip.jsx';
import FormInput from 'components/FormInput.jsx';
import TooltipTrigger from 'components/TooltipTrigger.jsx';

import './styles/PasswordStrengthField.scss';

type Props = {
  name: string,
  label: string,
  onScoreUpdated?: (score: number, variant: ?string) => void,
};

type State = {
  score: number,
  password: string,
  zxcvbn?: (password: string) => Object,
};

const SCORE_MAPPINGS = [
  { description: 'Weak', level: 'weak' }, // 0
  { description: 'Weak', level: 'weak' }, // 1
  { description: 'Good', level: 'good' }, // 2
  { description: 'Good', level: 'good' }, // 3
  { description: 'Strong', level: 'strong' }, // 4
];

const UNSCORED = -1;
const MIN_LENGTH = 6;

export default class PasswordStrengthField extends React.Component<Props, State> {
  state: State = {
    password: '',
    score: UNSCORED,
    zxcvbn: undefined,
  };

  componentDidMount() {
    this.loadZXCVBN();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.onScoreUpdated) {
      if (this.state.score !== prevState.score) {
        this.props.onScoreUpdated(this.state.score);
      }
    }
  }

  loadZXCVBN() {
    // https://github.com/airbnb/babel-plugin-dynamic-import-node/issues/47
    // webpack and node resolve the dynamic module differently
    import(/* webpackChunkName: "zxcvbn" */ 'zxcvbn')
      .then(zxcvbn => this.setState({ zxcvbn: zxcvbn.default || zxcvbn }))
      .catch(e => console.error('password strength field: could not load zxcvbn library: ', e));
  }

  handleChange = (event: *) => {
    const password = event.target.value;
    const score = this.state.zxcvbn ? this.state.zxcvbn(password).score : UNSCORED;
    this.setState({ password, score });
  };

  render() {
    const { score, password } = this.state;
    const { name, label } = this.props;

    let level;
    let description;

    if (score > UNSCORED) {
      ({ level, description } = SCORE_MAPPINGS[score]);

      if (password.length < MIN_LENGTH) {
        description = 'Too short';
      }
    }

    const classnames = ['PasswordStrengthField'];

    if (password.length === 0) {
      classnames.push('PasswordStrengthField--empty');
    } else if (password.length < MIN_LENGTH) {
      classnames.push('PasswordStrengthField--score-too-short');
    } else if (level) {
      classnames.push(`PasswordStrengthField--score-${level}`);
    }

    return (
      <div className={cx(classnames)}>
        <FormInput
          required
          name={name}
          label={label}
          type="password"
          validOnPristine
          value={password}
          validations="minLength:6"
          autoComplete="new-password"
          onChange={this.handleChange}
          className="PasswordStrengthField-input"
          data-test="PasswordStrengthField-input"
          validationError={
            // when scored, we show the validation message in the description.
            score === UNSCORED ? 'Password must be at least 6 characters long.' : undefined
          }
        />
        {score !== UNSCORED && (
          <React.Fragment>
            <div className="PasswordStrengthField-strength-bar" />
            <TooltipTrigger
              placement="top"
              tooltip={
                <Tooltip>
                  Good passwords are hard to guess. Use uncommon words or inside jokes, non-standard
                  uppercasing, creative spelling, and non-obvious numbers and symbols.
                </Tooltip>
              }>
              <span className="PasswordStrengthField-strength-desc">{description}</span>
            </TooltipTrigger>
          </React.Fragment>
        )}
      </div>
    );
  }
}
